import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { MapPin, Bed, Bath, Square, DollarSign, TrendingUp, Phone, Mail, ArrowLeft, Heart, ChevronLeft, ChevronRight, Building2, Calendar, Home, User, MessageCircle, Send, Wrench, Landmark, Users, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import AuthGate from '../components/AuthGate';
import RentalCalculator from '../components/RentalCalculator';
import VerifiedBadge from '../components/VerifiedBadge';
import DealStatusPill from '../components/DealStatusPill';
import { useAuth, API } from '../App';

const formatPrice = (price) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  }).format(price);
};

export default function DealDetailPage() {
  const { dealId } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [deal, setDeal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFavorited, setIsFavorited] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const [messageOpen, setMessageOpen] = useState(false);
  const [offerOpen, setOfferOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [offerAmount, setOfferAmount] = useState('');
  const [offerMessage, setOfferMessage] = useState('');
  const [inquiryForm, setInquiryForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  useEffect(() => {
    fetchDeal();
    if (token) {
      checkFavorite();
    }
  }, [dealId, token]);

  useEffect(() => {
    if (user) {
      setInquiryForm(prev => ({
        ...prev,
        name: user.name || '',
        email: user.email || ''
      }));
    }
  }, [user]);

  const fetchDeal = async () => {
    try {
      const response = await axios.get(`${API}/deals/${dealId}`);
      setDeal(response.data);
    } catch (error) {
      toast.error('Deal not found');
    } finally {
      setLoading(false);
    }
  };

  const checkFavorite = async () => {
    try {
      const response = await axios.get(`${API}/favorites/check/${dealId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsFavorited(response.data.favorited);
    } catch (error) {
      // Silently fail
    }
  };

  const handleToggleFavorite = async () => {
    if (!token) {
      toast.error('Please login to save favorites');
      return;
    }

    try {
      const response = await axios.post(`${API}/favorites`, { deal_id: dealId }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsFavorited(response.data.favorited);
      toast.success(response.data.favorited ? 'Added to favorites' : 'Removed from favorites');
    } catch (error) {
      toast.error('Failed to update favorite');
    }
  };

  const handleInquirySubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/inquiries`, {
        target_type: 'deal',
        target_id: dealId,
        ...inquiryForm
      }, token ? { headers: { Authorization: `Bearer ${token}` } } : {});
      
      toast.success('Inquiry sent! The poster will contact you soon.');
      setInquiryOpen(false);
      setInquiryForm({ name: user?.name || '', email: user?.email || '', phone: '', message: '' });
    } catch (error) {
      toast.error('Failed to send inquiry');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendMessage = async () => {
    if (!token) {
      toast.error('Please login to send messages');
      return;
    }
    if (!messageText.trim()) {
      toast.error('Please enter a message');
      return;
    }

    setSendingMessage(true);
    try {
      await axios.post(`${API}/conversations`, {
        recipient_id: deal.user_id,
        deal_id: dealId,
        message: messageText,
        message_type: 'message'
      }, { headers: { Authorization: `Bearer ${token}` } });

      toast.success('Message sent! Check your Messages tab for replies.');
      setMessageOpen(false);
      setMessageText('');
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleSendOffer = async () => {
    if (!token) {
      toast.error('Please login to make an offer');
      return;
    }
    if (!offerAmount || parseFloat(offerAmount) <= 0) {
      toast.error('Please enter a valid offer amount');
      return;
    }

    setSendingMessage(true);
    try {
      await axios.post(`${API}/conversations`, {
        recipient_id: deal.user_id,
        deal_id: dealId,
        message: offerMessage || `I would like to make an offer of $${parseFloat(offerAmount).toLocaleString()} for this property.`,
        message_type: 'offer',
        offer_amount: parseFloat(offerAmount)
      }, { headers: { Authorization: `Bearer ${token}` } });

      toast.success('Offer sent! Check your Messages tab for responses.');
      setOfferOpen(false);
      setOfferAmount('');
      setOfferMessage('');
    } catch (error) {
      toast.error('Failed to send offer');
    } finally {
      setSendingMessage(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-slate-200 rounded w-1/4" />
            <div className="aspect-video bg-slate-200 rounded-xl" />
            <div className="grid md:grid-cols-3 gap-8">
              <div className="md:col-span-2 space-y-4">
                <div className="h-8 bg-slate-200 rounded w-3/4" />
                <div className="h-32 bg-slate-200 rounded" />
              </div>
              <div className="h-64 bg-slate-200 rounded" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!deal) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="max-w-7xl mx-auto px-6 py-12 text-center">
          <h1 className="font-heading text-2xl font-bold text-slate-900 mb-4">Deal Not Found</h1>
          <p className="text-slate-600 mb-6">This deal may have been removed or is no longer available.</p>
          <Button asChild>
            <Link to="/marketplace">Browse All Deals</Link>
          </Button>
        </div>
      </div>
    );
  }

  // Helper to get full photo URL (handles both full URLs and relative API paths)
  const getPhotoUrl = (photo) => {
    if (!photo) return 'https://images.pexels.com/photos/106399/pexels-photo-106399.jpeg?auto=compress&cs=tinysrgb&w=1200';
    if (photo.startsWith('http')) return photo;
    // Relative path like /api/files/... - prepend API base URL
    return `${API.replace('/api', '')}${photo}`;
  };

  const photos = deal.photos?.length > 0 
    ? deal.photos.map(getPhotoUrl)
    : ['https://images.pexels.com/photos/106399/pexels-photo-106399.jpeg?auto=compress&cs=tinysrgb&w=1200'];

  // Defensive: ensure numeric fields have defaults
  const askingPrice = deal.asking_price || 0;
  const arv = deal.arv || 0;
  const rehabCost = deal.rehab_cost || 0;
  const sqft = deal.sqft || 0;
  
  const profit = arv - askingPrice - rehabCost;
  const profitMargin = askingPrice > 0 ? ((profit / askingPrice) * 100).toFixed(1) : '0.0';

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      
      <AuthGate title="Sign Up Free to View Deal Details">
      
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Back Button */}
        <Link to="/marketplace" className="inline-flex items-center text-slate-600 hover:text-slate-900 mb-6" data-testid="back-link">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Marketplace
        </Link>

        {/* Photo Gallery */}
        <div className="relative mb-8 rounded-2xl overflow-hidden bg-slate-900" data-testid="photo-gallery">
          <div className="aspect-video relative">
            <img
              src={photos[currentPhotoIndex]}
              alt={`${deal.title} - Photo ${currentPhotoIndex + 1}`}
              className="w-full h-full object-cover"
            />
            <div className="absolute top-4 left-4 flex gap-2 flex-wrap">
              <span className="bg-blue-700 text-white text-sm font-bold px-4 py-1.5 rounded-full">
                OFF MARKET
              </span>
              {deal.deal_type && (
                <span className="bg-white/90 text-slate-900 text-sm font-medium px-4 py-1.5 rounded-full capitalize">
                  {deal.deal_type.replace('_', ' ')}
                </span>
              )}
              <DealStatusPill status={deal.deal_status || 'available'} className="!text-sm !px-3 !py-1.5" />
            </div>
            
            {photos.length > 1 && (
              <>
                <button
                  onClick={() => setCurrentPhotoIndex(prev => prev === 0 ? photos.length - 1 : prev - 1)}
                  className="absolute left-4 top-1/2 -translate-y-1/2 p-2 bg-white/90 rounded-full hover:bg-white transition-colors"
                >
                  <ChevronLeft className="h-6 w-6" />
                </button>
                <button
                  onClick={() => setCurrentPhotoIndex(prev => prev === photos.length - 1 ? 0 : prev + 1)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-white/90 rounded-full hover:bg-white transition-colors"
                >
                  <ChevronRight className="h-6 w-6" />
                </button>
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                  {photos.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setCurrentPhotoIndex(i)}
                      className={`w-2 h-2 rounded-full transition-colors ${i === currentPhotoIndex ? 'bg-white' : 'bg-white/50'}`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Title & Location */}
            <div>
              <h1 className="font-heading text-3xl font-bold text-slate-900 mb-3" data-testid="deal-title">
                {deal.title}
              </h1>
              <div className="flex items-center gap-2 text-slate-600">
                <MapPin className="h-5 w-5" />
                <span>{deal.address}, {deal.city}, {deal.state} {deal.zip_code}</span>
              </div>
            </div>

            {/* Quick Stats - only show if data exists */}
            {(deal.bedrooms || deal.bathrooms || sqft || deal.property_type) && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {deal.bedrooms && (
              <div className="bg-white p-4 rounded-xl border border-slate-200 text-center">
                <Bed className="h-6 w-6 text-slate-400 mx-auto mb-2" />
                <div className="text-2xl font-bold text-slate-900">{deal.bedrooms}</div>
                <div className="text-sm text-slate-500">Bedrooms</div>
              </div>
              )}
              {deal.bathrooms && (
              <div className="bg-white p-4 rounded-xl border border-slate-200 text-center">
                <Bath className="h-6 w-6 text-slate-400 mx-auto mb-2" />
                <div className="text-2xl font-bold text-slate-900">{deal.bathrooms}</div>
                <div className="text-sm text-slate-500">Bathrooms</div>
              </div>
              )}
              {sqft > 0 && (
              <div className="bg-white p-4 rounded-xl border border-slate-200 text-center">
                <Square className="h-6 w-6 text-slate-400 mx-auto mb-2" />
                <div className="text-2xl font-bold text-slate-900">{sqft.toLocaleString()}</div>
                <div className="text-sm text-slate-500">Sq Ft</div>
              </div>
              )}
              <div className="bg-white p-4 rounded-xl border border-slate-200 text-center">
                <Building2 className="h-6 w-6 text-slate-400 mx-auto mb-2" />
                <div className="text-lg font-bold text-slate-900">{deal.property_type}</div>
                <div className="text-sm text-slate-500">Type</div>
              </div>
            </div>
            )}

            {/* Financial Analysis - only show if we have financial data */}
            {(askingPrice > 0 || arv > 0 || rehabCost > 0) && (
            <div className="bg-white p-6 rounded-xl border border-slate-200">
              <h2 className="font-heading text-xl font-semibold text-slate-900 mb-6">Deal Analysis</h2>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <div className="text-sm text-slate-500 mb-1">Asking Price</div>
                  <div className="text-2xl font-bold text-slate-900">{formatPrice(askingPrice)}</div>
                </div>
                {arv > 0 && (
                <div className="p-4 bg-green-50 rounded-lg">
                  <div className="text-sm text-green-600 mb-1">ARV (After Repair Value)</div>
                  <div className="text-2xl font-bold text-green-700">{formatPrice(arv)}</div>
                </div>
                )}
                {rehabCost > 0 && (
                <div className="p-4 bg-amber-50 rounded-lg">
                  <div className="text-sm text-amber-600 mb-1">Estimated Rehab</div>
                  <div className="text-2xl font-bold text-amber-700">{formatPrice(rehabCost)}</div>
                </div>
                )}
              </div>
              
              {(arv > 0 && rehabCost > 0) && (
              <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm text-blue-600 mb-1">Estimated Profit</div>
                    <div className="text-3xl font-bold text-blue-700">{formatPrice(profit)}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-blue-600 mb-1">ROI</div>
                    <div className="text-3xl font-bold text-blue-700 flex items-center gap-1">
                      <TrendingUp className="h-6 w-6" />
                      {profitMargin}%
                    </div>
                  </div>
                </div>
              </div>
              )}

              {deal.rental_estimate && (
                <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600">Rental Estimate</span>
                    <span className="font-semibold text-slate-900">{formatPrice(deal.rental_estimate)}/mo</span>
                  </div>
                </div>
              )}
            </div>
            )}

            {/* Rental cashflow calculator - NextFlip differentiator */}
            {askingPrice > 0 && (
              <RentalCalculator
                askingPrice={askingPrice}
                initialRent={deal.rental_estimate || 0}
              />
            )}

            {/* Property Details */}
            <div className="bg-white p-6 rounded-xl border border-slate-200">
              <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">Property Details</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="flex items-center justify-between py-3 border-b border-slate-100">
                  <span className="text-slate-600">Property Type</span>
                  <span className="font-medium text-slate-900">{deal.property_type}</span>
                </div>
                {deal.occupancy_status && (
                <div className="flex items-center justify-between py-3 border-b border-slate-100">
                  <span className="text-slate-600">Occupancy</span>
                  <span className="font-medium text-slate-900">{deal.occupancy_status}</span>
                </div>
                )}
                {deal.lot_size && (
                <div className="flex items-center justify-between py-3 border-b border-slate-100">
                  <span className="text-slate-600">Lot Size</span>
                  <span className="font-medium text-slate-900">{deal.lot_size}</span>
                </div>
                )}
                {deal.deal_type && (
                <div className="flex items-center justify-between py-3 border-b border-slate-100">
                  <span className="text-slate-600">Deal Type</span>
                  <span className="font-medium text-slate-900 capitalize">{deal.deal_type?.replace('_', ' ')}</span>
                </div>
                )}
              </div>
            </div>

            {/* Description */}
            {deal.description && (
            <div className="bg-white p-6 rounded-xl border border-slate-200">
              <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">Description</h2>
              <p className="text-slate-600 leading-relaxed whitespace-pre-wrap">{deal.description}</p>
            </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Action Card */}
            <div className="bg-white p-6 rounded-xl border border-slate-200 sticky top-6">
              <div className="text-center mb-6">
                <div className="text-sm text-slate-500 mb-1">Asking Price</div>
                <div className="font-heading text-4xl font-bold text-slate-900" data-testid="asking-price">
                  {formatPrice(askingPrice)}
                </div>
              </div>

              {/* Make Offer Button */}
              <Dialog open={offerOpen} onOpenChange={setOfferOpen}>
                <DialogTrigger asChild>
                  <Button className="w-full bg-green-600 hover:bg-green-700 rounded-full mb-3" size="lg" data-testid="make-offer-btn">
                    <DollarSign className="h-5 w-5 mr-2" />
                    Make an Offer
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Make an Offer</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div>
                      <Label htmlFor="offer-amount">Offer Amount ($)</Label>
                      <Input
                        id="offer-amount"
                        type="number"
                        required
                        value={offerAmount}
                        onChange={(e) => setOfferAmount(e.target.value)}
                        placeholder="Enter your offer"
                        className="mt-1 text-lg"
                      />
                    </div>
                    <div>
                      <Label htmlFor="offer-message">Message (optional)</Label>
                      <Textarea
                        id="offer-message"
                        rows={3}
                        value={offerMessage}
                        onChange={(e) => setOfferMessage(e.target.value)}
                        placeholder="Add any terms or conditions..."
                        className="mt-1"
                      />
                    </div>
                    <Button 
                      onClick={handleSendOffer} 
                      disabled={sendingMessage || !offerAmount} 
                      className="w-full bg-green-600 hover:bg-green-700 rounded-full"
                    >
                      {sendingMessage ? 'Sending...' : 'Send Offer'}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>

              {/* Message Poster Button */}
              <Dialog open={messageOpen} onOpenChange={setMessageOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="w-full rounded-full mb-3" size="lg" data-testid="message-poster-btn">
                    <MessageCircle className="h-5 w-5 mr-2" />
                    Message Poster
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Send a Message</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div className="bg-slate-50 p-3 rounded-lg">
                      <p className="text-sm text-slate-600">About: <span className="font-medium text-slate-900">{deal.title}</span></p>
                    </div>
                    <div>
                      <Label htmlFor="message-text">Your Message</Label>
                      <Textarea
                        id="message-text"
                        required
                        rows={4}
                        value={messageText}
                        onChange={(e) => setMessageText(e.target.value)}
                        placeholder="Hi, I'm interested in this property..."
                        className="mt-1"
                      />
                    </div>
                    <Button 
                      onClick={handleSendMessage} 
                      disabled={sendingMessage || !messageText.trim()} 
                      className="w-full bg-[#5DADE2] hover:bg-[#4A9BD1] rounded-full"
                    >
                      {sendingMessage ? 'Sending...' : 'Send Message'}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>

              <Button
                variant="outline"
                className="w-full rounded-full"
                onClick={handleToggleFavorite}
                data-testid="save-btn"
              >
                <Heart className={`h-5 w-5 mr-2 ${isFavorited ? 'fill-red-500 text-red-500' : ''}`} />
                {isFavorited ? 'Saved' : 'Save Deal'}
              </Button>

              {/* Owner-only status controls */}
              {user && (user.user_id === deal.user_id || user.role === 'admin') && (
                <div className="mt-4 p-3 bg-slate-50 rounded-lg border border-slate-200" data-testid="owner-status-controls">
                  <div className="text-xs font-medium text-slate-600 mb-2">Update deal status</div>
                  <div className="grid grid-cols-3 gap-2">
                    {['available', 'under_contract', 'sold'].map((s) => {
                      const active = (deal.deal_status || 'available') === s;
                      const labels = { available: 'Available', under_contract: 'Under Contract', sold: 'Sold' };
                      return (
                        <button
                          key={s}
                          type="button"
                          onClick={async () => {
                            try {
                              await axios.put(
                                `${API}/deals/${dealId}/deal-status`,
                                { deal_status: s },
                                { headers: { Authorization: `Bearer ${token}` } }
                              );
                              setDeal((prev) => ({ ...prev, deal_status: s }));
                              toast.success('Deal status updated');
                            } catch (err) {
                              toast.error('Could not update status');
                            }
                          }}
                          className={`text-xs font-medium py-2 px-2 rounded-md border transition-colors ${
                            active
                              ? 'bg-blue-600 text-white border-blue-600'
                              : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-100'
                          }`}
                          data-testid={`set-deal-status-${s}`}
                        >
                          {labels[s]}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="mt-6 pt-6 border-t border-slate-200">
                <h3 className="font-semibold text-slate-900 mb-3">Posted By</h3>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
                    <User className="h-5 w-5 text-slate-500" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-slate-900">{deal.contact_name}</span>
                      {deal.poster_verified && <VerifiedBadge size="sm" />}
                    </div>
                    <div className="text-sm text-slate-500">Deal Poster</div>
                  </div>
                </div>
                <div className="space-y-2 text-sm">
                  <a href={`mailto:${deal.contact_email}`} className="flex items-center gap-2 text-slate-600 hover:text-blue-700">
                    <Mail className="h-4 w-4" />
                    {deal.contact_email}
                  </a>
                  <a href={`tel:${deal.contact_phone}`} className="flex items-center gap-2 text-slate-600 hover:text-blue-700">
                    <Phone className="h-4 w-4" />
                    {deal.contact_phone}
                  </a>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-slate-200 text-center">
                <div className="flex items-center justify-center gap-2 text-sm text-slate-500">
                  <Calendar className="h-4 w-4" />
                  Listed {new Date(deal.created_at).toLocaleDateString()}
                </div>
              </div>

              {/* Directory Promotion */}
              <div className="mt-6 pt-6 border-t border-slate-200">
                <h3 className="font-semibold text-slate-900 mb-4 text-center">Need Help With This Deal?</h3>
                <div className="space-y-3">
                  <Link 
                    to="/directory?category=Realtor" 
                    className="flex items-center gap-3 p-3 bg-gradient-to-r from-blue-50 to-blue-100/50 rounded-xl hover:from-blue-100 hover:to-blue-200/50 transition-all group"
                    data-testid="promo-realtor"
                  >
                    <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Users className="h-5 w-5 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-slate-900 text-sm">Find a Realtor</div>
                      <div className="text-xs text-slate-500">Help analyzing this deal</div>
                    </div>
                    <ArrowRight className="h-4 w-4 text-slate-400 group-hover:text-blue-600 group-hover:translate-x-1 transition-all" />
                  </Link>

                  <Link 
                    to="/directory?category=Contractor" 
                    className="flex items-center gap-3 p-3 bg-gradient-to-r from-amber-50 to-amber-100/50 rounded-xl hover:from-amber-100 hover:to-amber-200/50 transition-all group"
                    data-testid="promo-contractor"
                  >
                    <div className="w-10 h-10 bg-amber-600 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Wrench className="h-5 w-5 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-slate-900 text-sm">Find a Contractor</div>
                      <div className="text-xs text-slate-500">Get rehab estimates</div>
                    </div>
                    <ArrowRight className="h-4 w-4 text-slate-400 group-hover:text-amber-600 group-hover:translate-x-1 transition-all" />
                  </Link>

                  <Link 
                    to="/directory?category=Lender" 
                    className="flex items-center gap-3 p-3 bg-gradient-to-r from-green-50 to-green-100/50 rounded-xl hover:from-green-100 hover:to-green-200/50 transition-all group"
                    data-testid="promo-lender"
                  >
                    <div className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Landmark className="h-5 w-5 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-slate-900 text-sm">Find a Lender</div>
                      <div className="text-xs text-slate-500">Fund this deal</div>
                    </div>
                    <ArrowRight className="h-4 w-4 text-slate-400 group-hover:text-green-600 group-hover:translate-x-1 transition-all" />
                  </Link>
                </div>

                <Link 
                  to="/directory" 
                  className="mt-4 flex items-center justify-center gap-2 text-sm text-[#5DADE2] hover:text-blue-700 font-medium"
                >
                  Browse All Providers
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
      </AuthGate>

      <Footer />
    </div>
  );
}
